// THE hotel stay, as the operator console sees it (feature `stay-nights`, slice N8).
//
// Until this slice the console never showed a night count, because it never had
// one: every stay it booked was a client-side `now → now + 24h` literal, written
// out three separate times (`searchHotels`, `use-holds` confirm, the hotel desk's
// resolve panel). A vendor prices `perNight × nights`, so a passenger stranded
// three days was searched for, priced and booked as one night. Those three
// literals are gone; this module is where the window comes from instead.
//
// WHAT THIS FILE IS
//   - the read-only MIRROR of `operator-bff:IStayPlanView` / `IStayMismatchView`
//     (`apps/operator-bff/src/modules/stay/stay.types.ts`), which mirror
//     `cases:ISubCase.stayPlan@v1`. cases owns the schema; we code to it.
//   - the ONE client-side fallback for a group cases has no plan for — `DEFAULT`,
//     `now → now + 24h`, one night. stay-nights.md §6 invariant 4: a group with no
//     new evidence must book byte-for-byte as it did before.
//   - the pure arithmetic behind the disruption-level Nights control's
//     pre-confirm conflict count (§7).
//
// WHAT THIS FILE IS NOT
// It does NOT compute a night count. Nights are hotel-LOCAL calendar days, which
// needs the overnight station's UTC offset; that arithmetic lives exactly once, in
// `@nexa/resolution-engine`'s `computeStayPlan` behind the cases `StayPlanService`
// (§6 invariant 3). A browser re-deriving it from two ISO instants would produce a
// second, quietly different answer — which is the bug this feature exists to kill.
// `nights` is therefore READ, never computed, and absent rather than guessed.
//
// Copy lives in i18n; this file holds KEYS, never language (`i18n.test.ts` /
// `no-hardcoded-copy.test.ts`).

/**
 * Which rung of the §7 ladder produced a night count. Ordered by authority —
 * explicit human input outranks derived evidence, because the operator can see
 * what was derived and is the one holding the outside context.
 *
 * Precedence, first match wins:
 * `OPERATOR_GROUP → OPERATOR_DISRUPTION → REBOOKING → MANIFEST → DEFAULT`.
 */
export type StayPlanSource =
  | 'OPERATOR_GROUP'
  | 'OPERATOR_DISRUPTION'
  | 'REBOOKING'
  | 'MANIFEST'
  | 'DEFAULT';

/** The flight the window was sized from — the plan's audit trail. */
export interface StayBasisFlight {
  flightNumber: string;
  /** ISO-8601 instant — an absolute departure, NOT a calendar date. */
  departureUtc: string;
}

/** `cases:ISubCase.stayPlan@v1` as the console reads it. */
export interface StayPlanView {
  checkInUtc: string;
  checkOutUtc: string;
  /** >= 1. Hotel-LOCAL calendar days, counted by cases — never re-derived here. */
  nights: number;
  source: StayPlanSource;
  basisFlight?: StayBasisFlight;
  /** The derived count exceeded `care.stay.maxNights` and cases clamped it. */
  cappedByPolicy?: boolean;
}

/**
 * `cases:ISubCase.stayMismatch@v1` — a night-count change that landed once a hotel
 * was already at stake. Raised ONLY when a hold or reservation exists; with nothing
 * booked a re-derivation applies silently.
 *
 * Everything but the two counts is optional: a cases build that predates a field
 * must still surface the conflict rather than hide it — the operator being TOLD is
 * the point (§6 invariant 5, money never moves unattended).
 */
export interface StayMismatchView {
  /** Nights the existing hold / reservation was made for. */
  bookedNights: number;
  /** Nights the ladder now derives. */
  derivedNights: number;
  derivedSource?: StayPlanSource;
  /** `HELD` | `CONFIRMED` upstream; kept loose so a widened vocabulary cannot make
   *  this mirror reject a real conflict. */
  reservationStatus?: string;
  /** `AUTO_ADJUSTED` — policy moved the plan and this record IS the audit entry.
   *  `OPERATOR` (the default) — the plan was LEFT ALONE; a human resolves it. */
  resolution?: 'AUTO_ADJUSTED' | 'OPERATOR';
  reason?: 'CONFIRMED_RESERVATION' | 'BEYOND_MAX_NIGHTS' | 'POLICY_OPERATOR' | 'POLICY_AUTO';
  detectedAt?: string;
}

/** One PNR group's stay, as `GET /v1/cases/:urn/allocation` returns it. */
export interface CaseStayGroupView {
  subCaseUrn: string;
  /** Friendly PNR label (`urn:pnr:ABC123:…` → `ABC123`). */
  pnr: string;
  stayPlan: StayPlanView | null;
  stayMismatch: StayMismatchView | null;
}

/**
 * The case's hotel stay (`operator-bff:ICaseStayView`). Groups cases has no plan
 * for are COUNTED, not defaulted: `withoutPlan` is how the view says "these still
 * book a 1-night stay" instead of implying a number nobody made.
 */
export interface CaseStayView {
  groups: CaseStayGroupView[];
  /** The night count when EVERY planned group agrees on one; `null` when they
   *  differ (a mixed case is a real state) or no group has a plan at all. */
  nights: number | null;
  mismatches: number;
  withoutPlan: number;
  withPlan: number;
}

/** 200 body of both nights routes (`operator-bff:IStayNightsResult`). */
export interface StayNightsResult {
  scope: 'DISRUPTION' | 'GROUP';
  disruptionUrn: string;
  subCaseUrn?: string;
  /** What the operator set. `null` = a GROUP override was cleared. */
  nights: number | null;
  stayPlan?: StayPlanView;
  stayMismatch?: StayMismatchView;
  /** DISRUPTION scope: how many PNR groups cases re-derived. */
  affected?: number;
  /** How many touched groups now carry a `stayMismatch`. */
  mismatched?: number;
  /** DISRUPTION scope: groups that KEPT their own per-PNR number — the §7 figure
   *  the control shows BEFORE confirming, so good data is visibly never erased. */
  overridden?: number;
}

/** `operator-bff:WS StayPlanChanged@v1` — the nights change, fanned to the room. */
export interface StayPlanChangedEvent extends StayNightsResult {
  byUrn?: string;
  byName?: string;
  at?: string;
}

/**
 * The hotel's own clock for a stay. A plan's `checkInUtc` is when the window was
 * DERIVED — `now` on most rungs — not when the hotel hands over the room, so
 * printing its wall clock told the desk a room was ready at 10:22. Neither booking
 * nor the vendor feeds we read state a per-hotel time yet (ETG's `check_in_time`
 * is not forwarded), so the console prints the booked hotel-local DATES at the
 * standard hotel clock: in from 15:00, out by 12:00.
 */
export const HOTEL_CHECK_IN_HOUR = 15;
export const HOTEL_CHECK_OUT_HOUR = 12;

/** `15` → "15:00" — the 24 h face the boards use. */
export function hotelClock(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/** One night, in milliseconds. The `DEFAULT` rung — today's behaviour, unchanged. */
export const DEFAULT_STAY_MS = 24 * 60 * 60 * 1000;

/**
 * Structural ceiling on what an operator may type, mirroring `cases`'
 * `STAY_NIGHTS_MAX` so a fat-fingered `999` is caught at the keyboard instead of
 * travelling to a 400. It is NOT the business bound — `care.stay.maxNights`
 * (engine default 14) is policy's, applied server-side and reported back as
 * `cappedByPolicy`. Copying THAT number here would give a policy rule a second
 * home; this one exists only so the input has an `max` attribute.
 */
export const STAY_NIGHTS_MAX = 60;
export const STAY_NIGHTS_MIN = 1;

/** i18n KEY (namespace `cases`) naming each rung of the ladder for the operator. */
export const STAY_SOURCE_KEY: Record<StayPlanSource, string> = {
  OPERATOR_GROUP: 'stayPlan.source.operatorGroup',
  OPERATOR_DISRUPTION: 'stayPlan.source.operatorDisruption',
  REBOOKING: 'stayPlan.source.rebooking',
  MANIFEST: 'stayPlan.source.manifest',
  DEFAULT: 'stayPlan.source.default',
};

/** A parseable ISO-8601 instant. Anything else → null (never propagated). */
function instant(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/** The window one booking/search call is made with, plus WHERE it came from. */
export interface StayWindow {
  checkInUtc: string;
  checkOutUtc: string;
  /** Nights when KNOWN — from the plan cases derived, or `1` on the `DEFAULT`
   *  rung (a `now → now + 24h` window IS one night). Never guessed. */
  nights?: number;
  source: StayPlanSource;
  basisFlight?: StayBasisFlight;
  cappedByPolicy?: boolean;
}

/**
 * The stay window to search / book ONE group with — the single client-side
 * derivation site, and the replacement for the three deleted 24h literals.
 *
 * @param plan the group's `stayPlan`, when cases derived one
 * @param now  injectable clock: one fan-out captures ONE `now` and passes it for
 *             every group, so the DEFAULT groups of a run share an identical
 *             window instead of drifting apart across a hundred `new Date()`s.
 *
 * A plan missing either end is NOT half-honoured — booking and the hotels search
 * both hard-require the pair — it falls through to DEFAULT.
 */
export function stayWindow(plan?: StayPlanView | null, now: Date = new Date()): StayWindow {
  const checkIn = instant(plan?.checkInUtc);
  const checkOut = instant(plan?.checkOutUtc);
  if (plan && checkIn && checkOut) {
    return {
      checkInUtc: checkIn.toISOString(),
      checkOutUtc: checkOut.toISOString(),
      ...(typeof plan.nights === 'number' && plan.nights >= 1 ? { nights: plan.nights } : {}),
      source: plan.source ?? 'DEFAULT',
      ...(plan.basisFlight ? { basisFlight: plan.basisFlight } : {}),
      ...(plan.cappedByPolicy === true ? { cappedByPolicy: true } : {}),
    };
  }
  // DEFAULT — `now → now + 24h`, one night. Byte-for-byte what the three deleted
  // literals produced, so a group with no evidence books exactly as before.
  return {
    checkInUtc: now.toISOString(),
    checkOutUtc: new Date(now.getTime() + DEFAULT_STAY_MS).toISOString(),
    nights: 1,
    source: 'DEFAULT',
  };
}

/**
 * A resolved window rendered as a plan, so a group with NO cases-derived plan
 * still shows the operator what it is about to book (the `DEFAULT` rung, one
 * night, starting now) instead of showing nothing and reading as "unknown".
 */
export function windowAsPlan(win: StayWindow): StayPlanView {
  return {
    checkInUtc: win.checkInUtc,
    checkOutUtc: win.checkOutUtc,
    nights: win.nights ?? 0,
    source: win.source,
    ...(win.basisFlight ? { basisFlight: win.basisFlight } : {}),
    ...(win.cappedByPolicy ? { cappedByPolicy: true } : {}),
  };
}

/**
 * Read a `stayPlan` off a value whose type does not declare one.
 *
 * Several rows this slice must display nights on are typed in files outside its
 * ownedGlobs (`lib/disruptions/types.ts`, `lib/bulk-allocation/types.ts`). Rather
 * than widen a shared type behind a serialised wiring lock, we narrow the wire at
 * runtime: the BFF is already sending the field, and a build that isn't simply
 * yields `undefined` and the caller shows nothing. Validated on the two fields
 * everything renders from, so a malformed push is dropped at the door instead of
 * printing `NaN nights` at an operator.
 */
export function readStayPlan(value: unknown): StayPlanView | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const plan = (value as { stayPlan?: unknown }).stayPlan;
  return narrowPlan(plan);
}

/** Same, for `stayMismatch`. */
export function readStayMismatch(value: unknown): StayMismatchView | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const m = (value as { stayMismatch?: unknown }).stayMismatch;
  if (!m || typeof m !== 'object') return undefined;
  const v = m as { bookedNights?: unknown; derivedNights?: unknown };
  if (typeof v.bookedNights !== 'number' || typeof v.derivedNights !== 'number') return undefined;
  return m as StayMismatchView;
}

/**
 * Read the stay off an allocation-PLAN row, which carries it FLAT
 * (`checkInUtc` / `checkOutUtc` / `nights` / `staySource` / `stayBasisFlight` /
 * `stayCappedByPolicy` — `operator-bff:IAllocationPlanItem`) rather than nested
 * under `stayPlan`. Two shapes for one concept is the BFF's call, not ours; this
 * is where the console absorbs the difference instead of spreading it.
 *
 * `nights` is optional on that row by design (a caller-pinned raw window states
 * instants, not a night count) — so a row with a window but no count yields a plan
 * with `nights: 0`, which every renderer here treats as "unknown", never as zero.
 */
export function readPlanRowStay(value: unknown): StayPlanView | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as {
    checkInUtc?: unknown;
    checkOutUtc?: unknown;
    nights?: unknown;
    staySource?: unknown;
    stayBasisFlight?: unknown;
    stayCappedByPolicy?: unknown;
  };
  if (typeof v.checkInUtc !== 'string' || typeof v.checkOutUtc !== 'string') return undefined;
  return {
    checkInUtc: v.checkInUtc,
    checkOutUtc: v.checkOutUtc,
    nights: typeof v.nights === 'number' && v.nights >= 1 ? v.nights : 0,
    source: isSource(v.staySource) ? v.staySource : 'DEFAULT',
    ...(isBasisFlight(v.stayBasisFlight) ? { basisFlight: v.stayBasisFlight } : {}),
    ...(v.stayCappedByPolicy === true ? { cappedByPolicy: true } : {}),
  };
}

function narrowPlan(plan: unknown): StayPlanView | undefined {
  if (!plan || typeof plan !== 'object') return undefined;
  const v = plan as { checkInUtc?: unknown; checkOutUtc?: unknown; nights?: unknown; source?: unknown };
  if (typeof v.checkInUtc !== 'string' || typeof v.checkOutUtc !== 'string') return undefined;
  if (typeof v.nights !== 'number' || !Number.isFinite(v.nights) || v.nights < 1) return undefined;
  return { ...(plan as StayPlanView), source: isSource(v.source) ? v.source : 'DEFAULT' };
}

const SOURCES: readonly StayPlanSource[] = [
  'OPERATOR_GROUP',
  'OPERATOR_DISRUPTION',
  'REBOOKING',
  'MANIFEST',
  'DEFAULT',
];

function isSource(value: unknown): value is StayPlanSource {
  return typeof value === 'string' && (SOURCES as readonly string[]).includes(value);
}

function isBasisFlight(value: unknown): value is StayBasisFlight {
  if (!value || typeof value !== 'object') return false;
  const v = value as { flightNumber?: unknown; departureUtc?: unknown };
  return typeof v.flightNumber === 'string' && typeof v.departureUtc === 'string';
}

/** Was this night count derived from a FLIGHT, or stated by a human / defaulted? */
export function isFlightDerived(source: StayPlanSource): boolean {
  return source === 'REBOOKING' || source === 'MANIFEST';
}

/**
 * What a blanket "this disruption is N nights" would do — computed BEFORE the
 * operator confirms (stay-nights.md §7: "the UI shows the conflict count before
 * they confirm, so good data is never silently erased").
 *
 * Two DIFFERENT things are counted, because they have different consequences:
 *
 *   `conflicts`  groups whose FLIGHT-DERIVED evidence (REBOOKING / MANIFEST) says
 *                a different number. A blanket N *does* overwrite these — this is
 *                the number that should give the operator pause.
 *   `overridden` groups a human already pinned by hand (`OPERATOR_GROUP`). These
 *                are NOT overwritten: the top rung outranks a blanket one. Shown
 *                so "why didn't my number apply to those?" is answered up front,
 *                and so it reconciles with the same figure cases reports after.
 *
 * `agreeing` + `unplanned` complete the picture (nothing surprising happens to
 * them), and `considered` is every group weighed.
 */
export interface StayConflictPreview {
  considered: number;
  conflicts: number;
  overridden: number;
  agreeing: number;
  unplanned: number;
}

export function previewStayConflicts(
  nights: number,
  groups: readonly Pick<CaseStayGroupView, 'stayPlan'>[],
): StayConflictPreview {
  let conflicts = 0;
  let overridden = 0;
  let agreeing = 0;
  let unplanned = 0;
  for (const g of groups) {
    const plan = g.stayPlan;
    if (!plan) {
      unplanned += 1;
      continue;
    }
    if (plan.source === 'OPERATOR_GROUP') {
      overridden += 1;
      continue;
    }
    if (plan.nights === nights) {
      agreeing += 1;
      continue;
    }
    if (isFlightDerived(plan.source)) conflicts += 1;
    else agreeing += 1; // a previous blanket number / the default — no evidence lost
  }
  return { considered: groups.length, conflicts, overridden, agreeing, unplanned };
}

/**
 * The nights every planned group agrees on, or `null` when they differ / none has
 * a plan. Same rule the BFF applies per case (`ICaseStayView.nights`), applied
 * here across a whole disruption — a mixed event is a real state (some groups fly
 * tomorrow, some in three days) and must read as "mixed", never as an average.
 */
export function agreedNights(groups: readonly Pick<CaseStayGroupView, 'stayPlan'>[]): number | null {
  const seen = new Set<number>();
  for (const g of groups) if (g.stayPlan) seen.add(g.stayPlan.nights);
  return seen.size === 1 ? [...seen][0]! : null;
}

/** Local calendar date of an instant, e.g. "Aug 26" — the operator's own zone. */
export function formatStayDate(iso: string, locale?: string): string {
  const at = instant(iso);
  if (!at) return '';
  return at.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/** "Aug 26 → Aug 29" — the window, in the operator's zone. '' if unparseable. */
export function formatStayRange(checkInUtc: string, checkOutUtc: string, locale?: string): string {
  const from = formatStayDate(checkInUtc, locale);
  const to = formatStayDate(checkOutUtc, locale);
  return from && to ? `${from} \u2192 ${to}` : '';
}
